import apiService from './apiService';

class AuthService {
  /**
   * Register a new user account.
   *
   * The server:
   *   1. Validates that the email is not already on file
   *   2. Persists the user record
   *   3. Generates a 6-digit verification code and emails it
   *
   * POST /users/register
   *
   * Request body:
   *   { name, email, phone, street, city, state, country, zip }
   *
   * Note: `location` ({ lat, lng }) is server-generated by geocoding the
   * address fields — it is NOT sent by the client.
   *
   * Success response (HTTP 200):
   *   { "message": "User created successfully" }
   *
   * Error responses:
   *   HTTP 409 – { error: 'User already exists.' }
   *   HTTP 500/503 – { error: '...' }
   */
  async register(formData) {
    try {
      return await apiService.post('/users/register', {
        name:    formData.name,
        email:   formData.email,
        phone:   formData.phone,
        city:    formData.city,
        state:   formData.state,
        country: formData.country,
        zip:     formData.zip,
        street:  formData.street,
      });
    } catch (error) {
      console.warn('Registration API unavailable — using mock flow:', error.message);
      return { mock: true, message: 'User created successfully' };
    }
  }

  /**
   * Verify the 6-digit code emailed during registration.
   * On success the server returns a bearer token — the user is now logged in.
   * We persist the token to localStorage immediately.
   *
   * POST /users/verify
   *
   * Request body: { email, code }
   *
   * Success response (HTTP 200):
   *   { success: true, token: "<hex>", user: { id, name, email, phone,
   *     street, city, state, country, zip,
   *     location: { lat, lng } | null,
   *     validated, created_date } }
   *
   * Error responses:
   *   HTTP 400 – { success: false, error: 'email and code are required' }
   *   HTTP 401 – { success: false, error: 'Invalid or expired code' }
   *   HTTP 404 – { success: false, error: 'User not found' }
   */
  async verifyEmail(email, code) {
    try {
      const data = await apiService.post('/users/verify', { email, code });
      if (data.token) {
        localStorage.setItem('token', data.token);
        // Fetch the full profile so the caller gets a complete user object,
        // and so the async gap lets React commit context state before navigation.
        const profile = await this.getMe();
        return { ...data, user: profile.user };
      }
      return data;
    } catch (error) {
      console.warn('Verify-email API unavailable — using mock flow:', error.message);
      return { success: true, mock: true, token: null, user: { email } };
    }
  }

  /**
   * Request a 6-digit login code to be emailed to the user.
   * Always returns 200 regardless of whether the email is on file
   * (anti-enumeration). Show the same "check your email" UI either way.
   *
   * POST /users/request-login-code
   *
   * Request body: { email }
   *
   * Success response (HTTP 200):
   *   { "message": "If that email is registered, a code has been sent." }
   */
  async login(email) {
    try {
      return await apiService.post('/users/request-login-code', { email });
    } catch (error) {
      console.warn('Login API unavailable — using mock flow:', error.message);
      return { mock: true, message: 'If that email is registered, a code has been sent.' };
    }
  }

  /**
   * Exchange the 6-digit code for a bearer token.
   * Saves the token to localStorage and fetches the full user profile
   * via GET /users/me before returning, so callers get a complete user object.
   *
   * POST /users/login
   *
   * Request body: { email, code }
   *
   * Success response (HTTP 200):
   *   { email, token: "<hex>", timestamp: "YYYY-MM-DD HH:MM:SS" }
   *   (full user profile appended as `user` after the /users/me call)
   *   user shape: { id, name, email, phone, street, city, state, country, zip,
   *                 location: { lat, lng } | null, validated, created_date }
   *
   * Error responses:
   *   HTTP 400 – { error: 'bad request.' }
   *   HTTP 401 – { error: 'Invalid credentials' }
   */
  async verifyLogin(email, code) {
    try {
      const data = await apiService.post('/users/login', { email, code });
      if (data.token) {
        localStorage.setItem('token', data.token);
        // Fetch full profile so callers don't need a separate round-trip
        const profile = await this.getMe();
        return { ...data, user: profile.user };
      }
      return data;
    } catch (error) {
      if (error.status) {
        // HTTP error from the server (e.g. 401 wrong code) — pass it through
        // so Login.js can display the server's message inline.
        throw error;
      }
      // Network / service failure — tag it so Login.js can show a toast
      // rather than an inline form error.
      throw Object.assign(
        new Error('Login service not available'),
        { serviceUnavailable: true }
      );
    }
  }

  /**
   * Fetch the authenticated user's full profile.
   * Use this on app startup to validate a stored token and rehydrate state.
   *
   * GET /users/me
   * Headers: Authorization: Bearer <token>
   *
   * Success response (HTTP 200):
   *   { user: { id, name, email, phone, street, city, state, country, zip,
   *             location: { lat, lng } | null,
   *             validated, created_date } }
   *
   * Error responses:
   *   HTTP 401 – { error: '...' } — token missing, invalid, revoked, or expired
   */
  async getMe() {
    return await apiService.get('/users/me');
    // No mock fallback here — a failure means the token is invalid/missing,
    // and AuthContext should treat that as "not logged in".
  }

  /**
   * Update the authenticated user's personal information.
   * Email is not updatable (it is the auth identity).
   * After updating address fields the server re-geocodes to refresh location.
   *
   * PUT /users/me
   * Headers: Authorization: Bearer <token>
   *
   * Request body:
   *   { name, phone, street, city, state, country, zip }
   *
   * Success response (HTTP 200):
   *   { user: { id, name, email, phone, street, city, state, country, zip,
   *             location: { lat, lng } | null, validated, created_date } }
   */
  async updateUser(formData) {
    return await apiService.put('/users/me', {
      name:    formData.name,
      phone:   formData.phone,
      street:  formData.street,
      city:    formData.city,
      state:   formData.state,
      country: formData.country,
      zip:     formData.zip,
    });
  }

  /**
   * Revoke the current bearer token server-side and clear it from localStorage.
   * Always clears the local token regardless of the server response.
   *
   * POST /users/logout
   * Headers: Authorization: Bearer <token>
   *
   * Success response (HTTP 200):
   *   { "message": "You are logged out." }
   */
  async logout() {
    try {
      return await apiService.post('/users/logout', {});
    } catch (error) {
      console.warn('Logout API unavailable — clearing local session anyway:', error.message);
      return { mock: true };
    } finally {
      localStorage.removeItem('token');
    }
  }
}

export default new AuthService();

